// src/pages/videos/components/Sidebar.jsx
import React from "react";
import { Spinner, Button } from "react-bootstrap";
import VideoCard from "./VideoCard";

// format duration helper
function formatDuration(sec) {
    if (!sec || isNaN(sec)) return "";
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor(sec % 60);
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
}

// --- Modern overlay used on small desktop thumbs (same visual as cards) ---
function ModernLockOverlay() {
    return (
        <div className="lock-modern">
            <div className="lock-badge">
                <i className="fa-solid fa-lock" />
            </div>
            <div className="lock-hint d-md-none">Unlock to watch</div>
        </div>
    );
}

// === YOUR ORANGE PULSER, EXACT BEHAVIOR ===
const PulseStyles = () => (
    <style>{`
    .now-playing-dot {
      position: absolute;
      top: 6px;
      right: 6px;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #ff7a00;
      box-shadow: 0 0 0 0 rgba(255, 122, 0, 0.7);
      animation: mgd-orange-pulse 1.6s ease-out infinite;
      z-index: 20;
      pointer-events: none;
    }
    @keyframes mgd-orange-pulse {
      0% { box-shadow: 0 0 0 0 rgba(255, 122, 0, 0.7); transform: scale(1); }
      70% { box-shadow: 0 0 0 10px rgba(255, 122, 0, 0); transform: scale(1.05); }
      100% { box-shadow: 0 0 0 0 rgba(255, 122, 0, 0); transform: scale(1); }
    }
    /* small image progress bar */
    .thumb-progress { position:absolute; left:0; right:0; bottom:0; height:3px; background: rgba(255,255,255,.25); }
    .thumb-progress-inner { height:3px; background:#ff6a00; }
  `}</style>
);

// fallback progress for mobile when progressMap missing
function getProgressPercentFallback(slug) {
    try {
        const raw = localStorage.getItem(`videoProgress:${slug}`);
        const p = raw ? JSON.parse(raw) : null;
        if (p?.duration > 0) {
            const pct = Math.round((p.seconds / p.duration) * 100);
            return pct >= 5 ? pct : 0;
        }
        return 0;
    } catch {
        return 0;
    }
}

export default function Sidebar({
                                    auth, slug, scrollRef, sentinelRef,
                                    videos, visibleCount, progressMap = {},
                                    isPaging, hasMore, loadMore,
                                    isMobileLike, observerSupported, isScrollable
                                }) {
    const list = Array.isArray(videos) ? videos : [];
    const count = Number.isFinite(visibleCount) ? visibleCount : list.length;
    const toShow = list.slice(0, count);
    const more = typeof hasMore === "function" ? hasMore() : false;

    if (!list.length) return <div className="text-center text-muted py-4">No related videos</div>;

    // ===== MOBILE VERSION — use full VideoCard (keeps autoplay controller) =====
    if (isMobileLike) {
        return (
            <div className="mt-4 px-2">
                <PulseStyles />

                {toShow.map((v) => {
                    const isCurrent = v.slug === slug;
                    return (
                        <div key={v.slug} className="mb-3 position-relative">
                            <VideoCard video={v} isLocked={!auth?.user?.planType && !v.free} disabledPreview={true}/>
                            {isCurrent && <div className="now-playing-dot" />}
                        </div>
                    );
                })}

                <div ref={sentinelRef} />

                {isPaging && more && (
                    <div className="d-flex justify-content-center py-3">
                        <Spinner animation="border" size="sm" />
                    </div>
                )}

                {(!observerSupported || !isScrollable) && more && (
                    <div className="d-grid mt-3">
                        <Button variant="outline-secondary" onClick={loadMore}>Load more</Button>
                    </div>
                )}
            </div>
        );
    }

    // ===== DESKTOP / TABLET VERSION (compact list, no hover preview) =====
    return (
        <div ref={scrollRef} className="mt-4" style={{ maxHeight:"80vh", overflowY:"auto", paddingRight:4 }}>
            <PulseStyles />

            {toShow.map((v, i) => {
                const p = progressMap[v.slug];
                const pct = p && p.duration > 0
                    ? Math.min(100, Math.round((p.seconds / p.duration) * 100))
                    : getProgressPercentFallback(v.slug);

                const isCurrent = v.slug === slug;
                const isLocked = !auth?.user?.planType && !v.free;
                const durationLabel = formatDuration(v.durationSeconds);

                return (
                    <a key={i} style={{textDecoration:"none"}} href={`/videos/${v.slug}`} title={v.title}>
                        <div className="d-flex flex-row mb-2 sidebar-card">
                            <div className="d-inline-block px-2">
                                <div className={isLocked ? "position-relative" : "position-relative"}>
                                    <div style={{borderRadius:"4px", width:"150px", minHeight:"84px", position:'relative', background:"#111", overflow:"hidden"}}>
                                        {/* thumbnail */}
                                        <img
                                            src={v.thumbnail}
                                            alt={v.title}
                                            loading="lazy"
                                            decoding="async"
                                            width="150"
                                            height="84"
                                            style={{borderRadius:"4px", maxWidth:"150px", minHeight:"84px", objectFit:"cover", display:"block"}}
                                        />

                                        {/* duration pill */}
                                        {durationLabel && (
                                            <div
                                                className="position-absolute bottom-0 end-0 m-1 px-2 py-1 rounded text-white"
                                                style={{
                                                    background: "rgba(0,0,0,0.65)",
                                                    fontSize: "0.7rem",
                                                    lineHeight: 1,
                                                    pointerEvents: "none",
                                                    zIndex: 15
                                                }}
                                            >
                                                {durationLabel}
                                            </div>
                                        )}

                                        {/* now playing pulser */}
                                        {isCurrent && <div className="now-playing-dot" />}

                                        {/* modern lock overlay */}
                                        {isLocked && <ModernLockOverlay />}

                                        {/* tiny progress */}
                                        <div className="thumb-progress"><div className="thumb-progress-inner" style={{width:`${pct}%`}}/></div>
                                    </div>
                                </div>
                            </div>

                            <div className="d-inline-block align-top px-2" style={{flex:1, minWidth:0}}>
                                <h6 style={{fontSize:"14px", marginBottom:4}}>
                                    {v.title?.length > 60 ? `${v.title.slice(0,57)}...` : v.title}
                                </h6>
                                <p className="mb-1" style={{fontSize:12}}>{v.Category?.title}</p>
                            </div>
                        </div>
                    </a>
                );
            })}

            <div ref={sentinelRef} />

            {isPaging && more && (
                <div className="d-flex justify-content-center py-3"><Spinner animation="border" size="sm" /></div>
            )}

            {(!observerSupported || !isScrollable) && more && (
                <div className="d-grid mt-2 px-2">
                    <Button variant="outline-secondary" size="sm" onClick={loadMore}>Load more</Button>
                </div>
            )}
        </div>
    );
}
